import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import html2pdf from "html2pdf.js";
import { Pagination } from "../../components/ui/Pagination";

const API_BASE_URL = "/api/v1";

// Parse date string as local time (not UTC) to avoid timezone shift issues
// Updated: Force browser cache refresh v2
function parseLocalDate(dateStr: string): Date {
  if (!dateStr || typeof dateStr !== 'string') {
    return new Date();
  }
  const parts = dateStr.split('-');
  if (parts.length !== 3) {
    return new Date();
  }
  const [year, month, day] = parts.map(Number);
  return new Date(year, month - 1, day);
}

type ReportType = "employee" | "history" | "meals" | "tracking";
type DateMode = "single" | "range";
type MealType = "all" | "breakfast" | "lunch";

interface DeductionRecord {
  employee_number: string;
  type: string;
  account: string;
  effective_date: string;
  amount: number;
}

interface MissingEmployeeNumber {
  employee_name: string | null;
  user_id: number | null;
  account: string;
  dates: string[];
  meals: number;
  amount: number;
}

interface EmployeeOrder {
  employee_name: string;
  employee_number: string | null;
  meal_type: string;
  order_type: string;
  meal_name: string | null;
  toppings: string[] | null;
  notes: string | null;
}

interface PayPeriod {
  number: number;
  startDate: string;
  endDate: string;
  label: string;
}

// Generate all 26 pay periods starting from 12/21/2025
function generatePayPeriods(): PayPeriod[] {
  const periods: PayPeriod[] = [];
  // Use local time: year, month (0-indexed), day
  const startDate = new Date(2025, 11, 21); // Dec 21, 2025

  for (let i = 0; i < 26; i++) {
    const periodStart = new Date(startDate);
    periodStart.setDate(startDate.getDate() + (i * 14));

    const periodEnd = new Date(periodStart);
    periodEnd.setDate(periodStart.getDate() + 13);

    // Format as YYYY-MM-DD in local time
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    const formatLabel = (date: Date) => {
      const month = date.toLocaleDateString("en-US", { month: "short" });
      const day = date.getDate();
      return `${month} ${day}`;
    };

    periods.push({
      number: i + 1,
      startDate: formatDate(periodStart),
      endDate: formatDate(periodEnd),
      label: `Pay Period ${i + 1} (${formatLabel(periodStart)} - ${formatLabel(periodEnd)})`,
    });
  }

  return periods;
}

const PAY_PERIODS = generatePayPeriods();

export function ReportsPage() {
  const [reportType, setReportType] = useState<ReportType>("employee");

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex" aria-label="Tabs">
          <button
            onClick={() => setReportType("employee")}
            className={`flex-1 py-4 px-6 text-center font-medium text-sm border-b-2 transition-colors ${
              reportType === "employee"
                ? "border-blue-500 text-blue-600 bg-blue-50"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            Employee List
          </button>
          <button
            onClick={() => setReportType("history")}
            className={`flex-1 py-4 px-6 text-center font-medium text-sm border-b-2 transition-colors ${
              reportType === "history"
                ? "border-blue-500 text-blue-600 bg-blue-50"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            Employee History
          </button>
          <button
            onClick={() => setReportType("meals")}
            className={`flex-1 py-4 px-6 text-center font-medium text-sm border-b-2 transition-colors ${
              reportType === "meals"
                ? "border-blue-500 text-blue-600 bg-blue-50"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            Meal Reports
          </button>
          <button
            onClick={() => setReportType("tracking")}
            className={`flex-1 py-4 px-6 text-center font-medium text-sm border-b-2 transition-colors ${
              reportType === "tracking"
                ? "border-blue-500 text-blue-600 bg-blue-50"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            Meal Tracking
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {reportType === "employee" && <EmployeeListReport />}
        {reportType === "history" && <EmployeeHistoryReport />}
        {reportType === "meals" && <MealReports />}
        {reportType === "tracking" && <MealTrackingReport />}
      </div>
    </div>
  );
}

// =============================================================================
// Employee List Report (with CSV Export in Deductions Format)
// =============================================================================

export function EmployeeListReport() {
  const [dateMode, setDateMode] = useState<DateMode>("single");
  const [singleDate, setSingleDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [mealType, setMealType] = useState<MealType>("all");
  const [selectedPayPeriod, setSelectedPayPeriod] = useState<string>("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const effectiveStart = dateMode === "single" ? singleDate : startDate;
  const effectiveEnd = dateMode === "single" ? singleDate : endDate;

  // Reset page when filters change
  const resetPage = () => setPage(1);

  // Query for deductions data (for CSV export)
  const { data: deductionsData, isLoading: deductionsLoading } = useQuery({
    queryKey: ["deductions", effectiveStart, effectiveEnd, mealType],
    queryFn: async () => {
      const token = sessionStorage.getItem("hmh_access_token");
      const url = new URL(`${API_BASE_URL}/admin/reports/deductions`, window.location.origin);
      url.searchParams.set("start_date", effectiveStart);
      url.searchParams.set("end_date", effectiveEnd);
      url.searchParams.set("meal_type", mealType);
      // The CSV export must contain every record - never a single page
      url.searchParams.set("page_size", "0");

      const response = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to fetch deductions");
      return response.json();
    },
  });

  // Query for employee list (detailed view with pagination)
  const { data: employeeData, isLoading: employeeLoading } = useQuery({
    queryKey: ["employee-orders", effectiveStart, effectiveEnd, mealType, page, pageSize],
    queryFn: async () => {
      const token = sessionStorage.getItem("hmh_access_token");
      // For date range, we need to fetch each day
      const dates: string[] = [];
      const current = parseLocalDate(effectiveStart);
      const endDate = parseLocalDate(effectiveEnd);
      while (current <= endDate) {
        // Format as local date YYYY-MM-DD
        const year = current.getFullYear();
        const month = String(current.getMonth() + 1).padStart(2, '0');
        const day = String(current.getDate()).padStart(2, '0');
        dates.push(`${year}-${month}-${day}`);
        current.setDate(current.getDate() + 1);
      }

      const allEmployees: (EmployeeOrder & { order_date: string })[] = [];

      // Fetch every day in full (max page size), then paginate the combined
      // list on the client. Passing the on-screen page number through to each
      // day's request truncated busy days and dropped whole days from view.
      for (const date of dates) {
        const url = new URL(`${API_BASE_URL}/admin/reports/employee-list`, window.location.origin);
        url.searchParams.set("order_date", date);
        url.searchParams.set("meal_type", mealType);
        url.searchParams.set("page", "1");
        url.searchParams.set("page_size", "500");

        const response = await fetch(url.toString(), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          data.employees.forEach((emp: EmployeeOrder) => {
            allEmployees.push({ ...emp, order_date: date });
          });
        }
      }

      const total = allEmployees.length;
      const totalPages = Math.max(1, Math.ceil(total / pageSize));
      const start = (page - 1) * pageSize;

      return {
        employees: allEmployees.slice(start, start + pageSize),
        total,
        page,
        page_size: pageSize,
        total_pages: totalPages
      };
    },
  });

  const missingNumbers: MissingEmployeeNumber[] = deductionsData?.missing_employee_numbers ?? [];

  const exportDeductionsCSV = () => {
    if (!deductionsData?.deductions) return;
    if (
      missingNumbers.length > 0 &&
      !window.confirm(
        `${missingNumbers.length} employee(s) have meals in this period but no employee number on file, ` +
          `so they are NOT in this CSV ($${deductionsData.missing_amount}). See the list under the totals. Export anyway?`
      )
    ) {
      return;
    }

    // Build CSV with proper quoting to preserve leading zeros
    let csv = "Employee #,Type,Account,Effective Date,Amount\n";

    deductionsData.deductions.forEach((record: DeductionRecord) => {
      // Use ="00123" format to force Excel to treat as text and preserve leading zeros
      csv += `="${record.employee_number}",${record.type},${record.account},${record.effective_date},${record.amount}\n`;
    });

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const filename = selectedPayPeriod
      ? `Deductions-PayPeriod${selectedPayPeriod}.csv`
      : dateMode === "single"
      ? `Deductions-${singleDate}.csv`
      : `Deductions-${startDate}-to-${endDate}.csv`;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handlePayPeriodChange = (periodNum: string) => {
    setSelectedPayPeriod(periodNum);
    if (periodNum) {
      const period = PAY_PERIODS[parseInt(periodNum) - 1];
      setDateMode("range");
      setStartDate(period.startDate);
      setEndDate(period.endDate);
    }
    resetPage();
  };

  const isLoading = deductionsLoading || employeeLoading;

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-4">Employee Order List</h2>

      {/* Controls */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Pay Period Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pay Period</label>
            <select
              value={selectedPayPeriod}
              onChange={(e) => handlePayPeriodChange(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 min-w-[280px]"
            >
              <option value="">Select Pay Period...</option>
              {PAY_PERIODS.map((period) => (
                <option key={period.number} value={period.number}>
                  {period.label}
                </option>
              ))}
            </select>
          </div>

          {/* Date Mode Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Selection</label>
            <div className="flex rounded-md overflow-hidden border border-gray-300">
              <button
                onClick={() => {
                  setDateMode("single");
                  setSelectedPayPeriod("");
                  resetPage();
                }}
                className={`px-4 py-2 text-sm font-medium ${
                  dateMode === "single"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                Single Day
              </button>
              <button
                onClick={() => {
                  setDateMode("range");
                  setSelectedPayPeriod("");
                  resetPage();
                }}
                className={`px-4 py-2 text-sm font-medium border-l border-gray-300 ${
                  dateMode === "range"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                Date Range
              </button>
            </div>
          </div>

          {/* Date Inputs */}
          {dateMode === "single" ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={singleDate}
                onChange={(e) => {
                  setSingleDate(e.target.value);
                  setSelectedPayPeriod("");
                  resetPage();
                }}
                className="border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setSelectedPayPeriod("");
                    resetPage();
                  }}
                  className="border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setSelectedPayPeriod("");
                    resetPage();
                  }}
                  className="border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
            </>
          )}

          {/* Meal Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Meal Type</label>
            <select
              value={mealType}
              onChange={(e) => {
                setMealType(e.target.value as MealType);
                resetPage();
              }}
              className="border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="all">All Meals</option>
              <option value="breakfast">Breakfast Only</option>
              <option value="lunch">Lunch Only</option>
            </select>
          </div>

          {/* Export Button */}
          <button
            onClick={exportDeductionsCSV}
            disabled={!deductionsData?.deductions || deductionsData.deductions.length === 0}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export Deductions CSV
          </button>
        </div>

        {/* Selected Pay Period Display */}
        {selectedPayPeriod && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
            <p className="text-sm text-blue-800">
              <strong>Selected:</strong> {PAY_PERIODS[parseInt(selectedPayPeriod) - 1].label}
              {" "}
              <span className="text-blue-600">
                ({PAY_PERIODS[parseInt(selectedPayPeriod) - 1].startDate} to {PAY_PERIODS[parseInt(selectedPayPeriod) - 1].endDate})
              </span>
            </p>
          </div>
        )}

      </div>

      {isLoading && <p className="text-gray-500">Loading...</p>}

      {/* Orders that cannot be deducted: no employee number on file (HMH-2212) */}
      {missingNumbers.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm font-semibold text-red-800">
            {missingNumbers.length} employee(s) have meals in this period but no employee number on file.
            These meals (${deductionsData.missing_amount}) are not in the deductions CSV.
          </p>
          <table className="min-w-full mt-3 text-sm">
            <thead>
              <tr className="text-left text-red-700">
                <th className="pr-4 py-1">Employee</th>
                <th className="pr-4 py-1">Account</th>
                <th className="pr-4 py-1">Dates</th>
                <th className="pr-4 py-1">Meals</th>
                <th className="py-1">Amount</th>
              </tr>
            </thead>
            <tbody className="text-red-900">
              {missingNumbers.map((m, idx) => (
                <tr key={idx}>
                  <td className="pr-4 py-1">{m.employee_name || "(no name)"}</td>
                  <td className="pr-4 py-1">{m.account}</td>
                  <td className="pr-4 py-1">{m.dates.join(", ")}</td>
                  <td className="pr-4 py-1">{m.meals}</td>
                  <td className="py-1">${m.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-red-700 mt-2">
            Ask IT to add the employee number (Employee ID in the directory) for these people, then export again.
          </p>
        </div>
      )}

      {/* Summary Stats */}
      {deductionsData && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-600 font-medium">Total Orders</p>
            <p className="text-2xl font-bold text-blue-800">{deductionsData.total_orders || deductionsData.total_records}</p>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <p className="text-sm text-orange-600 font-medium">Breakfast Orders</p>
            <p className="text-2xl font-bold text-orange-800">
              {deductionsData.breakfast_orders || deductionsData.deductions.filter((d: DeductionRecord) => d.account === "BREAKFAST MEAL").length}
            </p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-600 font-medium">Lunch Orders</p>
            <p className="text-2xl font-bold text-green-800">
              {deductionsData.lunch_orders || deductionsData.deductions.filter((d: DeductionRecord) => d.account === "LUNCH MEAL").length}
            </p>
          </div>
        </div>
      )}

      {/* Employee Table with Pagination */}
      {employeeData?.employees && employeeData.employees.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {dateMode === "range" && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee #</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Meal</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {employeeData.employees.map((emp, idx) => (
                  <tr key={idx} className={emp.meal_type === "breakfast" ? "bg-orange-50" : ""}>
                    {dateMode === "range" && (
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {parseLocalDate(emp.order_date).toLocaleDateString()}
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{emp.employee_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{emp.employee_number || "-"}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        emp.meal_type === "breakfast"
                          ? "bg-orange-100 text-orange-800"
                          : "bg-green-100 text-green-800"
                      }`}>
                        {emp.meal_type === "breakfast" ? "Breakfast" : "Lunch"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{emp.meal_name || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {emp.toppings && emp.toppings.length > 0 ? emp.toppings.join(", ") : emp.notes || "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={page}
            totalPages={employeeData.total_pages || 0}
            totalItems={employeeData.total || 0}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
          />
        </div>
      )}

      {employeeData?.employees && employeeData.employees.length === 0 && (
        <p className="text-gray-500 text-center py-8">No orders found for the selected date(s).</p>
      )}
    </div>
  );
}

// =============================================================================
// Meal Reports
// =============================================================================

interface MealItem {
  meal_type: string;
  item_name: string;
  order_type: string;
  count: number;
}

interface SaladTopping {
  name: string;
  category: string;
  count: number;
}

function MealReports() {
  const [dateMode, setDateMode] = useState<DateMode>("single");
  const [singleDate, setSingleDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [mealType, setMealType] = useState<MealType>("all");
  const [itemsPage, setItemsPage] = useState(1);
  const [itemsPageSize, setItemsPageSize] = useState(25);
  const [toppingsPage, setToppingsPage] = useState(1);
  const [toppingsPageSize, setToppingsPageSize] = useState(25);
  const [dailyPage, setDailyPage] = useState(1);
  const [dailyPageSize, setDailyPageSize] = useState(50);

  const effectiveStart = dateMode === "single" ? singleDate : startDate;
  const effectiveEnd = dateMode === "single" ? singleDate : endDate;

  const resetItemsPage = () => setItemsPage(1);
  const resetToppingsPage = () => setToppingsPage(1);
  const resetDailyPage = () => setDailyPage(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ["daily-count", effectiveStart, effectiveEnd, mealType, dailyPage, dailyPageSize],
    queryFn: async () => {
      const token = sessionStorage.getItem("hmh_access_token");
      const url = new URL(`${API_BASE_URL}/admin/reports/daily-count`, window.location.origin);
      url.searchParams.set("start_date", effectiveStart);
      url.searchParams.set("end_date", effectiveEnd);
      url.searchParams.set("meal_type", mealType);
      url.searchParams.set("page", dailyPage.toString());
      url.searchParams.set("page_size", dailyPageSize.toString());

      const response = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to fetch report");
      return response.json();
    },
  });

  // Query for meal items breakdown with pagination
  const { data: itemsData } = useQuery({
    queryKey: ["meal-items", effectiveStart, effectiveEnd, mealType, itemsPage, itemsPageSize],
    queryFn: async () => {
      const token = sessionStorage.getItem("hmh_access_token");
      const url = new URL(`${API_BASE_URL}/admin/reports/meal-items`, window.location.origin);
      url.searchParams.set("start_date", effectiveStart);
      url.searchParams.set("end_date", effectiveEnd);
      url.searchParams.set("meal_type", mealType);
      url.searchParams.set("page", itemsPage.toString());
      url.searchParams.set("page_size", itemsPageSize.toString());

      const response = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to fetch items");
      return response.json();
    },
  });

  // Query for salad toppings breakdown with pagination
  const { data: toppingsData } = useQuery({
    queryKey: ["salad-toppings", effectiveStart, effectiveEnd, toppingsPage, toppingsPageSize],
    queryFn: async () => {
      const token = sessionStorage.getItem("hmh_access_token");
      const url = new URL(`${API_BASE_URL}/admin/reports/salad-toppings`, window.location.origin);
      url.searchParams.set("start_date", effectiveStart);
      url.searchParams.set("end_date", effectiveEnd);
      url.searchParams.set("page", toppingsPage.toString());
      url.searchParams.set("page_size", toppingsPageSize.toString());

      const response = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to fetch toppings");
      return response.json();
    },
    enabled: mealType === "all" || mealType === "lunch",
  });

  // Calculate totals
  const totals = {
    breakfast: 0,
    lunch: 0,
    salad: 0,
    special: 0,
    total: 0,
  };

  if (data?.data) {
    if (Array.isArray(data.data)) {
      // New format for 'all' or 'breakfast' meal_type
      data.data.forEach((item: any) => {
        if (item.meal_type === "breakfast") {
          totals.breakfast += item.total || 0;
        } else if (item.meal_type === "lunch") {
          // totals.lunch += item.total || 0; // REMOVED - was double counting
          totals.lunch += item.lunch || 0;
          totals.salad += item.salad || 0;
          totals.special += item.special || 0;
        }
        totals.total += item.total || 0;
      });
    } else {
      // Old format for 'lunch' meal_type
      Object.values(data.data).forEach((counts: any) => {
        totals.lunch += counts.lunch || 0;
        totals.salad += counts.salad || 0;
        totals.special += counts.special || 0;
        totals.total += counts.total || 0;
        // totals.lunch += counts.total || 0; // REMOVED - was double counting
      });
    }
  }

  const reportRef = useRef<HTMLDivElement>(null);

  const exportToPDF = () => {
    if (!reportRef.current) return;

    const filename = dateMode === "single"
      ? `Meal-Report-${singleDate}.pdf`
      : `Meal-Report-${startDate}-to-${endDate}.pdf`;

    const opt = {
      margin: 0.5,
      filename: filename,
      image: { type: "jpeg" as const, quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" as const },
    };

    html2pdf().set(opt).from(reportRef.current).save();
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-4">Meal Reports</h2>

      {/* Controls */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Date Mode Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Selection</label>
            <div className="flex rounded-md overflow-hidden border border-gray-300">
              <button
                onClick={() => {
                  setDateMode("single");
                  resetItemsPage();
                  resetToppingsPage();
                  resetDailyPage();
                }}
                className={`px-4 py-2 text-sm font-medium ${
                  dateMode === "single"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                Single Day
              </button>
              <button
                onClick={() => {
                  setDateMode("range");
                  resetItemsPage();
                  resetToppingsPage();
                  resetDailyPage();
                }}
                className={`px-4 py-2 text-sm font-medium border-l border-gray-300 ${
                  dateMode === "range"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                Date Range
              </button>
            </div>
          </div>

          {/* Date Inputs */}
          {dateMode === "single" ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={singleDate}
                onChange={(e) => {
                  setSingleDate(e.target.value);
                  resetItemsPage();
                  resetToppingsPage();
                  resetDailyPage();
                }}
                className="border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    resetItemsPage();
                    resetToppingsPage();
                    resetDailyPage();
                  }}
                  className="border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    resetItemsPage();
                    resetToppingsPage();
                    resetDailyPage();
                  }}
                  className="border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
            </>
          )}

          {/* Meal Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Meal Type</label>
            <select
              value={mealType}
              onChange={(e) => {
                setMealType(e.target.value as MealType);
                resetItemsPage();
                resetToppingsPage();
                resetDailyPage();
              }}
              className="border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="all">All Meals</option>
              <option value="breakfast">Breakfast Only</option>
              <option value="lunch">Lunch Only</option>
            </select>
          </div>

          {/* Export Button */}
          <button
            onClick={exportToPDF}
            disabled={!data?.data}
            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            Export PDF
          </button>
        </div>
      </div>

      {isLoading && <p className="text-gray-500">Loading...</p>}
      {error && <p className="text-red-600">Error loading data</p>}

      {/* Report Content - wrapped for PDF export */}
      <div ref={reportRef} className="bg-white p-4">
        {/* Report Header for PDF */}
        <div className="text-center mb-6 print:block hidden-screen">
          <h1 className="text-2xl font-bold">Hood Memorial Hospital</h1>
          <h2 className="text-lg">Meal Report</h2>
          <p className="text-sm text-gray-600">
            {dateMode === "single"
              ? parseLocalDate(singleDate).toLocaleDateString()
              : `${parseLocalDate(startDate).toLocaleDateString()} - ${parseLocalDate(endDate).toLocaleDateString()}`
            }
          </p>
        </div>

        {/* Summary Cards */}
        {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-600 font-medium">Total Orders</p>
            <p className="text-3xl font-bold text-blue-800">{totals.total}</p>
          </div>
          {(mealType === "all" || mealType === "breakfast") && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-sm text-orange-600 font-medium">Breakfast</p>
              <p className="text-3xl font-bold text-orange-800">{totals.breakfast}</p>
            </div>
          )}
          {(mealType === "all" || mealType === "lunch") && (
            <>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-600 font-medium">Lunch</p>
                <p className="text-3xl font-bold text-green-800">{totals.lunch}</p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-600 font-medium">Special</p>
                <p className="text-3xl font-bold text-yellow-800">{totals.special}</p>
              </div>
              <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                <p className="text-sm text-purple-600 font-medium">Salads</p>
                <p className="text-3xl font-bold text-purple-800">{totals.salad}</p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Items Ordered Breakdown */}
      {itemsData?.items && itemsData.items.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Items Ordered</h3>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Meal Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order Type</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Count</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {itemsData.items.map((item: MealItem, idx: number) => (
                    <tr key={idx} className={item.meal_type === "breakfast" ? "bg-orange-50" : ""}>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          item.meal_type === "breakfast"
                            ? "bg-orange-100 text-orange-800"
                            : "bg-green-100 text-green-800"
                        }`}>
                          {item.meal_type === "breakfast" ? "Breakfast" : "Lunch"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{item.item_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{item.order_type || "Regular"}</td>
                      <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">{item.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={itemsPage}
              totalPages={itemsData.total_pages || 0}
              totalItems={itemsData.total || 0}
              pageSize={itemsPageSize}
              onPageChange={setItemsPage}
              onPageSizeChange={(newSize) => {
                setItemsPageSize(newSize);
                setItemsPage(1);
              }}
            />
          </div>
        </div>
      )}

      {/* Salad Toppings Breakdown */}
      {(mealType === "all" || mealType === "lunch") && toppingsData?.toppings && toppingsData.toppings.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Salad Toppings Ordered</h3>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Topping Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Count</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {toppingsData.toppings.map((topping: SaladTopping, idx: number) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{topping.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
                          {topping.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">{topping.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={toppingsPage}
              totalPages={toppingsData.total_pages || 0}
              totalItems={toppingsData.total || 0}
              pageSize={toppingsPageSize}
              onPageChange={setToppingsPage}
              onPageSizeChange={(newSize) => {
                setToppingsPageSize(newSize);
                setToppingsPage(1);
              }}
            />
          </div>
        </div>
      )}

      {/* Daily Breakdown Table */}
      {data?.data && (
        <div className="overflow-x-auto">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Daily Breakdown</h3>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Meal</th>
                {(mealType === "all" || mealType === "lunch") && (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lunch</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Salad</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Special</th>
                  </>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(data.data) ? (
                data.data.map((item: any, idx: number) => (
                  <tr key={idx} className={item.meal_type === "breakfast" ? "bg-orange-50" : ""}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {parseLocalDate(item.date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        item.meal_type === "breakfast"
                          ? "bg-orange-100 text-orange-800"
                          : "bg-green-100 text-green-800"
                      }`}>
                        {item.meal_type === "breakfast" ? "Breakfast" : "Lunch"}
                      </span>
                    </td>
                    {(mealType === "all" || mealType === "lunch") && (
                      <>
                        <td className="px-4 py-3 text-sm text-gray-500">{item.lunch || "-"}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{item.salad || "-"}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{item.special || "-"}</td>
                      </>
                    )}
                    <td className="px-4 py-3 text-sm font-bold text-gray-900">{item.total || 0}</td>
                  </tr>
                ))
              ) : (
                Object.entries(data.data).sort().map(([date, counts]: [string, any]) => (
                  <tr key={date}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {parseLocalDate(date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Lunch
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{counts.lunch || 0}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{counts.salad || 0}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{counts.special || 0}</td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900">{counts.total || 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {data && (
            <Pagination
              currentPage={dailyPage}
              totalPages={data.total_pages || 0}
              totalItems={data.total || 0}
              pageSize={dailyPageSize}
              onPageChange={setDailyPage}
              onPageSizeChange={(newSize) => {
                setDailyPageSize(newSize);
                setDailyPage(1);
              }}
            />
          )}
        </div>
      )}
      </div>{/* End of reportRef div */}
    </div>
  );
}

// =============================================================================
// Meal Tracking Report
// =============================================================================

interface MealTrackingData {
  date: string;
  patient: number;
  employee: number;
  guest: number;
  doctor: number;
  pec: number;
  daily_total: number;
}

interface ManualMealEntry {
  id: number;
  date: string;
  meal_type: string;
  count: number;
  notes: string | null;
  created_at: string;
}

type ManualMealType = "patient" | "doctor" | "pec";

function MealTrackingReport() {
  const queryClient = useQueryClient();
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Form state
  const [formDate, setFormDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [formMealType, setFormMealType] = useState<ManualMealType>("patient");
  const [formCount, setFormCount] = useState("");
  const [formNotes, setFormNotes] = useState("");

  const resetPage = () => setPage(1);

  // Fetch meal tracking data with pagination
  const { data: trackingData, isLoading: trackingLoading, error: trackingError } = useQuery({
    queryKey: ["meal-tracking", startDate, endDate, page, pageSize],
    queryFn: async () => {
      const token = sessionStorage.getItem("hmh_access_token");
      const url = new URL(`${API_BASE_URL}/admin/reports/meal-tracking`, window.location.origin);
      url.searchParams.set("start_date", startDate);
      url.searchParams.set("end_date", endDate);
      url.searchParams.set("page", page.toString());
      url.searchParams.set("page_size", pageSize.toString());

      const response = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to fetch meal tracking data");
      return response.json();
    },
  });

  // Fetch manual meal entries (patient/doctor/pec)
  const { data: manualMealsData, isLoading: manualMealsLoading } = useQuery({
    queryKey: ["patient-meals", startDate, endDate],
    queryFn: async () => {
      const token = sessionStorage.getItem("hmh_access_token");
      const url = new URL(`${API_BASE_URL}/admin/patient-meals`, window.location.origin);
      url.searchParams.set("start_date", startDate);
      url.searchParams.set("end_date", endDate);

      const response = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to fetch manual meals");
      return response.json();
    },
  });

  // Mutation for adding manual meals
  const addManualMealMutation = useMutation({
    mutationFn: async (data: { date: string; meal_type: string; count: number; notes?: string }) => {
      const token = sessionStorage.getItem("hmh_access_token");
      const response = await fetch(`${API_BASE_URL}/admin/patient-meals`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to add meal entry");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meal-tracking"] });
      queryClient.invalidateQueries({ queryKey: ["patient-meals"] });
      setShowAddForm(false);
      setFormDate(new Date().toISOString().split("T")[0]);
      setFormMealType("patient");
      setFormCount("");
      setFormNotes("");
    },
  });

  const handleSubmitManualMeal = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formDate || !formCount || parseInt(formCount) <= 0) {
      alert("Please provide a valid date and count");
      return;
    }
    addManualMealMutation.mutate({
      date: formDate,
      meal_type: formMealType,
      count: parseInt(formCount),
      notes: formNotes || undefined,
    });
  };

  // Get summary from API response
  const summary = trackingData?.summary || {
    patient_meals: 0,
    employee_meals: 0,
    guest_meals: 0,
    doctor_meals: 0,
    pec_meals: 0,
    total_meals: 0,
  };

  const getMealTypeLabel = (mealType: string) => {
    switch (mealType) {
      case "patient": return "Patient";
      case "doctor": return "Doctor";
      case "pec": return "PEC";
      case "breakfast": return "Patient (Legacy)";
      case "lunch": return "Patient (Legacy)";
      default: return mealType;
    }
  };

  const getMealTypeColor = (mealType: string) => {
    switch (mealType) {
      case "patient":
      case "breakfast":
      case "lunch":
        return "bg-purple-100 text-purple-800";
      case "doctor":
        return "bg-indigo-100 text-indigo-800";
      case "pec":
        return "bg-pink-100 text-pink-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-4">Meal Tracking</h2>

      {/* Controls */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                resetPage();
              }}
              className="border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                resetPage();
              }}
              className="border border-gray-300 rounded-md px-3 py-2"
            />
          </div>

          {/* Add Meals Button */}
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Meals
          </button>
        </div>
      </div>

      {/* Add Manual Meal Form */}
      {showAddForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Add Meal Count</h3>
          <form onSubmit={handleSubmitManualMeal} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={formDate}
                  onChange={(e) => setFormDate(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={formMealType}
                  onChange={(e) => setFormMealType(e.target.value as ManualMealType)}
                  className="border border-gray-300 rounded-md px-3 py-2 w-full"
                  required
                >
                  <option value="patient">Patient</option>
                  <option value="doctor">Doctor</option>
                  <option value="pec">PEC</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Count</label>
                <input
                  type="number"
                  min="1"
                  value={formCount}
                  onChange={(e) => setFormCount(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 w-full"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <input
                type="text"
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 w-full"
                placeholder="Any additional notes..."
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={addManualMealMutation.isPending}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {addManualMealMutation.isPending ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
            {addManualMealMutation.isError && (
              <p className="text-red-600 text-sm">
                Error: {addManualMealMutation.error?.message || "Failed to add meal entry"}
              </p>
            )}
          </form>
        </div>
      )}

      {trackingLoading && <p className="text-gray-500">Loading...</p>}
      {trackingError && <p className="text-red-600">Error loading meal tracking data</p>}

      {/* Summary Cards */}
      {trackingData && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <p className="text-sm text-purple-600 font-medium">Patient</p>
            <p className="text-3xl font-bold text-purple-800">{summary.patient_meals}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-600 font-medium">Employee</p>
            <p className="text-3xl font-bold text-blue-800">{summary.employee_meals}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-600 font-medium">Guest</p>
            <p className="text-3xl font-bold text-green-800">{summary.guest_meals}</p>
          </div>
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <p className="text-sm text-indigo-600 font-medium">Doctor</p>
            <p className="text-3xl font-bold text-indigo-800">{summary.doctor_meals}</p>
          </div>
          <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
            <p className="text-sm text-pink-600 font-medium">PEC</p>
            <p className="text-3xl font-bold text-pink-800">{summary.pec_meals}</p>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <p className="text-sm text-orange-600 font-medium">Total</p>
            <p className="text-3xl font-bold text-orange-800">{summary.total_meals}</p>
          </div>
        </div>
      )}

      {/* Daily Breakdown Table */}
      {trackingData?.daily_breakdown && trackingData.daily_breakdown.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Daily Breakdown</h3>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Patient</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Employee</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Guest</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Doctor</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">PEC</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Daily Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {trackingData.daily_breakdown.map((day: MealTrackingData, idx: number) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {parseLocalDate(day.date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-center text-gray-600">{day.patient}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-600">{day.employee}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-600">{day.guest}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-600">{day.doctor}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-600">{day.pec}</td>
                      <td className="px-4 py-3 text-sm text-center font-bold text-gray-900">{day.daily_total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={page}
              totalPages={trackingData.total_pages || 0}
              totalItems={trackingData.total || 0}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setPage(1);
              }}
            />
          </div>
        </div>
      )}

      {/* Manual Meal Entries */}
      {manualMealsData?.entries && manualMealsData.entries.length > 0 && (
        <div className="overflow-x-auto">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Manual Meal Entries</h3>
          <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Count</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {manualMealsData.entries.map((entry: ManualMealEntry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {parseLocalDate(entry.date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getMealTypeColor(entry.meal_type)}`}>
                      {getMealTypeLabel(entry.meal_type)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-600">{entry.count}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{entry.notes || "-"}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(entry.created_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {manualMealsLoading && <p className="text-gray-500">Loading manual meals...</p>}
    </div>
  );
}

// =============================================================================
// Employee History Report - one employee's orders across a date range
// =============================================================================

interface HistoryOrder {
  order_id: number;
  order_date: string;
  meal_period?: string;
  order_type: string;
  item: string | null;
  toppings: string[];
  notes: string | null;
  status: string;
  comped: boolean;
  created_at: string | null;
}

interface HistoryResponse {
  matches: { name: string; employee_number: string | null }[] | null;
  employee: { name: string; employee_number: string | null } | null;
  start_date?: string;
  end_date?: string;
  orders: HistoryOrder[];
  summary?: {
    total_orders: number;
    billable_orders: number;
    billable_amount: number;
    comped_orders: number;
    cancelled_orders: number;
  };
}

interface EmployeeSuggestion {
  name: string;
  employee_number: string | null;
}

export function EmployeeHistoryReport() {
  const today = new Date().toISOString().split("T")[0];
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().split("T")[0];

  const [employee, setEmployee] = useState("");
  const [startDate, setStartDate] = useState(monthAgo);
  const [endDate, setEndDate] = useState(today);
  const [result, setResult] = useState<HistoryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<EmployeeSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const skipSearchRef = useRef(false);

  // Search-as-you-type (debounced) against known users
  useEffect(() => {
    if (skipSearchRef.current) {
      skipSearchRef.current = false;
      return;
    }
    const q = employee.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const token = sessionStorage.getItem("hmh_access_token");
        const url = new URL(`${API_BASE_URL}/admin/reports/employee-search`, window.location.origin);
        url.searchParams.append("q", q);
        const response = await fetch(url.toString(), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setSuggestions(data.results || []);
          setShowSuggestions(true);
        }
      } catch {
        // suggestions are best-effort
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [employee]);

  const pickSuggestion = (s: EmployeeSuggestion) => {
    skipSearchRef.current = true;
    const who = s.employee_number || s.name;
    setEmployee(who);
    setSuggestions([]);
    setShowSuggestions(false);
    run(who);
  };

  const run = async (who?: string) => {
    const q = (who ?? employee).trim();
    if (q.length < 2 || !startDate || !endDate) return;
    if (who) {
      skipSearchRef.current = true;
      setEmployee(who);
    }
    setShowSuggestions(false);
    setLoading(true);
    setError(null);
    try {
      const token = sessionStorage.getItem("hmh_access_token");
      const url = new URL(`${API_BASE_URL}/admin/reports/employee-history`, window.location.origin);
      url.searchParams.append("employee", q);
      url.searchParams.append("start_date", startDate);
      url.searchParams.append("end_date", endDate);
      const response = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || "Failed to run report");
      }
      setResult(await response.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to run report");
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (!result?.employee || !result.orders.length) return;
    const esc = (v: string | null | undefined) => `"${(v ?? "").replace(/"/g, '""')}"`;
    const lines = [
      "Date,Meal Period,Type,Item,Toppings,Notes,Status,Comped",
      ...result.orders.map((o) =>
        [o.order_date, o.meal_period ?? "lunch", o.order_type, esc(o.item),
         esc(o.toppings.join("; ")), esc(o.notes), o.status, o.comped ? "yes" : "no"].join(",")
      ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `orders_${result.employee.employee_number || "employee"}_${startDate}_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const printReport = () => {
    if (!result?.employee) return;
    const esc = (v: string | null | undefined) =>
      (v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const s = result.summary;
    const rows = result.orders.map((o) => `
      <tr${o.status === "cancelled" ? ' class="cancelled"' : ""}>
        <td>${o.order_date}</td>
        <td>${esc(o.order_type)}${o.meal_period === "midnight" ? " (midnight)" : ""}</td>
        <td>${esc(o.item) || "-"}</td>
        <td>${esc(o.toppings.join(", "))}</td>
        <td>${esc(o.notes)}</td>
        <td>${esc(o.status)}${o.comped ? " (comped)" : ""}</td>
      </tr>`).join("");
    const html = `<!doctype html><html><head><title>Employee Order History</title><style>
      body { font-family: Arial, sans-serif; font-size: 12px; color: #222; margin: 24px; }
      h1 { font-size: 18px; margin: 0 0 2px 0; }
      .sub { color: #555; margin-bottom: 12px; }
      .totals { margin: 8px 0 16px 0; }
      table { border-collapse: collapse; width: 100%; }
      th, td { border: 1px solid #999; padding: 4px 8px; text-align: left; }
      th { background: #eee; }
      .cancelled { color: #999; text-decoration: line-through; }
    </style></head><body>
      <h1>Employee Order History</h1>
      <div class="sub">${esc(result.employee.name)}
        ${result.employee.employee_number ? "(#" + esc(result.employee.employee_number) + ")" : ""}
        &mdash; ${result.start_date} to ${result.end_date}</div>
      <div class="totals"><b>Orders:</b> ${s?.total_orders}
        &nbsp; <b>Billable:</b> ${s?.billable_orders} ($${s?.billable_amount})
        &nbsp; <b>Comped:</b> ${s?.comped_orders}
        &nbsp; <b>Cancelled:</b> ${s?.cancelled_orders}</div>
      <table><thead><tr><th>Date</th><th>Type</th><th>Item</th><th>Toppings</th><th>Notes</th><th>Status</th></tr></thead>
      <tbody>${rows}</tbody></table>
    </body></html>`;
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-1">Employee Order History</h2>
      <p className="text-sm text-gray-500 mb-4">
        Every order one employee placed in a date range - for payroll questions and disputes.
      </p>

      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">Employee (name or #)</label>
          <input
            type="text"
            value={employee}
            onChange={(e) => setEmployee(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder="Start typing a name or number..."
            className="border border-gray-300 rounded px-3 py-2 w-64 focus:outline-none focus:ring-2 focus:ring-hmh-blue"
          />
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute z-10 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickSuggestion(s)}
                  className="block w-full text-left px-3 py-2 text-sm hover:bg-blue-50"
                >
                  {s.name}{s.employee_number ? ` (#${s.employee_number})` : ""}
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-hmh-blue" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-hmh-blue" />
        </div>
        <button
          onClick={() => run()}
          disabled={loading || employee.trim().length < 2}
          className="px-4 py-2 bg-[#007FA3] text-white rounded hover:bg-[#005f7a] disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {loading ? "Running..." : "Run Report"}
        </button>
        {result?.employee && result.orders.length > 0 && (
          <>
            <button onClick={printReport}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50">
              Print
            </button>
            <button onClick={exportCSV}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50">
              Export CSV
            </button>
          </>
        )}
      </div>

      {error && <div className="text-red-600 mb-4">{error}</div>}

      {result?.matches && result.matches.length > 1 && (
        <div className="mb-4">
          <p className="text-sm text-gray-700 mb-2">Several people match - pick one:</p>
          <div className="flex flex-wrap gap-2">
            {result.matches.map((m, i) => (
              <button key={i}
                onClick={() => run(m.employee_number || m.name)}
                className="px-3 py-1.5 rounded-full text-sm border border-gray-300 hover:bg-blue-50">
                {m.name}{m.employee_number ? ` (#${m.employee_number})` : ""}
              </button>
            ))}
          </div>
        </div>
      )}
      {result?.matches && result.matches.length === 0 && (
        <p className="text-gray-500">No employee found matching "{employee}".</p>
      )}

      {result?.employee && (
        <>
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="bg-gray-50 px-4 py-2 rounded border border-gray-200">
              <div className="text-sm text-gray-600">{result.employee.name}</div>
              <div className="text-lg font-bold text-hmh-blue">
                #{result.employee.employee_number || "-"}
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-2 rounded border border-gray-200">
              <div className="text-sm text-gray-600">Orders</div>
              <div className="text-lg font-bold text-hmh-blue">{result.summary?.total_orders}</div>
            </div>
            <div className="bg-gray-50 px-4 py-2 rounded border border-gray-200">
              <div className="text-sm text-gray-600">Billable</div>
              <div className="text-lg font-bold text-hmh-blue">
                {result.summary?.billable_orders} (${result.summary?.billable_amount})
              </div>
            </div>
            {(result.summary?.comped_orders ?? 0) > 0 && (
              <div className="bg-gray-50 px-4 py-2 rounded border border-gray-200">
                <div className="text-sm text-gray-600">Comped (tickets)</div>
                <div className="text-lg font-bold text-hmh-blue">{result.summary?.comped_orders}</div>
              </div>
            )}
            {(result.summary?.cancelled_orders ?? 0) > 0 && (
              <div className="bg-gray-50 px-4 py-2 rounded border border-gray-200">
                <div className="text-sm text-gray-600">Cancelled</div>
                <div className="text-lg font-bold text-hmh-blue">{result.summary?.cancelled_orders}</div>
              </div>
            )}
          </div>

          {result.orders.length === 0 ? (
            <p className="text-gray-500">No orders in this date range.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {["Date", "Type", "Item", "Toppings", "Notes", "Status"].map((h) => (
                      <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {result.orders.map((o) => (
                    <tr key={o.order_id} className={o.status === "cancelled" ? "text-gray-400 line-through" : ""}>
                      <td className="px-4 py-2 text-sm whitespace-nowrap">{o.order_date}</td>
                      <td className="px-4 py-2 text-sm capitalize">
                        {o.order_type}{o.meal_period === "midnight" ? " (midnight)" : ""}
                      </td>
                      <td className="px-4 py-2 text-sm">{o.item || "-"}</td>
                      <td className="px-4 py-2 text-sm">{o.toppings.join(", ")}</td>
                      <td className="px-4 py-2 text-sm">{o.notes || ""}</td>
                      <td className="px-4 py-2 text-sm">
                        {o.status}{o.comped ? " (comped)" : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
